import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ForceGraph2D from "react-force-graph-2d";
import { forceX, forceY } from "d3-force";
import { motion, AnimatePresence } from "framer-motion";
import { api, INTENT_COLORS, STATUS_COLORS, fmtUsd } from "@/lib/nexus";
import AgentInspector from "@/components/AgentInspector";
import { Sparkles, Loader2, Settings2, X, ArrowRight, Terminal, Copy, Check, ZoomIn, ZoomOut, Maximize2, Eye, EyeOff, Focus, Layers } from "lucide-react";

const EXAMPLES = [
  "show me drain patterns from LangChain agents on Ethereum",
  "high risk agents in Singapore",
  "OpenAI orchestrators paying more than $1000",
  "critical CrewAI wallets on Solana",
  "collusion rings across any chain",
];

const GROUP_FIELDS = {
  framework: (n) => n.framework,
  chain: (n) => n.chain,
  status: (n) => n.status,
  region: (n) => n.region,
};
const GROUP_LABELS = { framework: "Framework", chain: "Chain", status: "Risk", region: "Region" };
const CLUSTER_TINTS = ["#00F0FF", "#9D4EDD", "#FFB000", "#22D3EE", "#F472B6", "#4ADE80", "#FB923C", "#818CF8"];

function Hero({ onRun, loading }) {
  const [q, setQ] = useState("");
  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6">
      <div className="max-w-2xl w-full text-center">
        <div className="panel-title mb-3">GRAPH WORKSPACE // NL INVESTIGATION</div>
        <h1 className="font-rajdhani font-bold text-4xl md:text-5xl tracking-tight leading-tight">
          Ask a question about the <span className="text-nexus-cyan">agentic economy.</span>
        </h1>
        <p className="text-nexus-text-2 mt-4 text-[14px] leading-relaxed">
          Tentacles translates plain-English intel questions into structured on-chain graph queries and
          traces the agent-to-agent commerce that answers them — surfacing what a human eye would never see.
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); if (q.trim()) onRun(q); }}
          className="mt-8 flex items-center gap-2 border border-nexus-border bg-nexus-panel/70 px-4 py-3 focus-within:border-nexus-violet/60"
        >
          <Sparkles size={16} className="text-nexus-violet shrink-0" />
          <input
            data-testid="hero-nlp-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ASK IN PLAIN ENGLISH…"
            className="bg-transparent text-[14px] font-mono uppercase tracking-widest outline-none flex-1 text-nexus-text placeholder:text-nexus-text-3"
          />
          {loading ? (
            <Loader2 size={16} className="animate-spin text-nexus-violet" />
          ) : (
            <button
              type="submit"
              data-testid="hero-nlp-submit"
              className="text-[11px] font-mono uppercase tracking-widest text-nexus-violet border border-nexus-violet/50 hover:bg-nexus-violet/10 px-3 py-1"
            >
              Trace
            </button>
          )}
        </form>

        <div className="mt-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3 mb-2">Try</div>
          <div className="flex flex-wrap gap-2 justify-center">
            {EXAMPLES.map((e) => (
              <button
                key={e}
                data-testid={`hero-example-${e.slice(0, 18)}`}
                onClick={() => onRun(e)}
                className="text-[11px] font-mono text-nexus-text-2 border border-nexus-border px-2.5 py-1 hover:text-nexus-cyan hover:border-nexus-cyan/50 transition"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReasoningPanel({ result }) {
  const [copied, setCopied] = useState(false);
  const curlCmd = `curl "${window.location.origin.replace(/\/$/, "")}${result.api_call.replace("GET ", "")}"`;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <div className="panel p-3 brackets">
        <span className="b1" /><span className="b2" />
        <div className="panel-title mb-2"><span className="dot" style={{ background: "#9D4EDD", boxShadow: "0 0 8px #9D4EDD" }} />Reasoning Trace</div>
        <div className="text-[12px] text-nexus-text-2 mb-3 italic">{result.explanation}</div>
        <div className="space-y-2">
          {result.reasoning_steps.map((s) => (
            <div key={s.step} className="border-l-2 border-nexus-violet/50 pl-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-nexus-violet">{s.step}</div>
              <div className="text-[12px] text-nexus-text mt-0.5">{s.detail}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="panel p-3 brackets">
          <span className="b1" /><span className="b2" />
          <div className="flex justify-between items-start">
            <div className="panel-title"><Terminal size={11} className="inline mr-1" />Generated API Call</div>
            <button
              data-testid="copy-api-call"
              onClick={() => { navigator.clipboard.writeText(curlCmd); setCopied(true); setTimeout(() => setCopied(false), 1400); }}
              className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/40 hover:bg-nexus-cyan/10 px-2 py-0.5 flex items-center gap-1"
            >
              {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <pre className="mt-2 font-mono text-[11px] leading-relaxed text-nexus-cyan whitespace-pre-wrap break-all border border-nexus-border/60 bg-nexus-bg/70 p-2">
            <span className="text-nexus-violet">$</span> {curlCmd}
          </pre>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(result.filters || {}).map(([k, v]) => (
              <span key={k} className="text-[10px] font-mono uppercase tracking-widest border border-nexus-cyan/40 text-nexus-cyan px-1.5 py-0.5">
                {k} = {String(v)}
              </span>
            ))}
          </div>
        </div>

        <div className="panel p-3 brackets">
          <span className="b1" /><span className="b2" />
          <div className="panel-title mb-2"><span className="dot" />Commerce Trace · Top A2A Hops</div>
          {result.commerce_trace.length === 0 ? (
            <div className="text-[12px] text-nexus-text-2">No agent-to-agent commerce edges match this query.</div>
          ) : (
            <div className="space-y-1.5">
              {result.commerce_trace.map((t, i) => (
                <div key={`${t.from}>${t.to}:${t.intent}:${t.value_usd}`} className="flex items-center justify-between gap-2 border border-nexus-border/60 px-2 py-1.5" data-testid={`commerce-hop-${i}`}>
                  <div className="flex items-center gap-1.5 min-w-0 text-[11px] font-mono">
                    <span className="text-nexus-text truncate">{t.from}</span>
                    <ArrowRight size={11} className="text-nexus-text-3 shrink-0" style={{ color: t.intent_color }} />
                    <span className="text-nexus-text truncate">{t.to}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[9px] font-mono uppercase tracking-widest border px-1 py-0.5" style={{ borderColor: t.intent_color, color: t.intent_color }}>
                      {t.intent}
                    </span>
                    <span className="text-[11px] font-mono text-nexus-text">{fmtUsd(t.value_usd)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultGraph({ nodes, links, onSelect, selectedId }) {
  const containerRef = useRef();
  const fgRef = useRef();
  const [dims, setDims] = useState({ w: 900, h: 480 });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const ro = new ResizeObserver((es) => { if (es[0]) setDims({ w: es[0].contentRect.width, h: es[0].contentRect.height }); });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!fgRef.current) return;
    const fg = fgRef.current;
    fg.d3Force("charge").strength(-260).distanceMax(220);
    const link = fg.d3Force("link");
    if (link) link.distance(70).strength(0.15);
    setTimeout(() => {
      // Cap max zoom so a tiny result set doesn't blow up node radius
      if (nodes.length <= 3) {
        fg.centerAt(0, 0, 400);
        fg.zoom(2.2, 400);
      } else {
        fg.zoomToFit(400, 80);
      }
    }, 400);
  }, [nodes, links, dims]);

  const graphData = useMemo(() => ({
    nodes: nodes.map((n) => ({ ...n })),
    links: links.map((l) => ({ ...l })),
  }), [nodes, links]);

  return (
    <div ref={containerRef} className="relative brackets border border-nexus-border bg-nexus-panel/30 overflow-hidden" style={{ height: 520 }}>
      <span className="b1" /><span className="b2" />
      <div className="absolute top-2 left-3 z-10 panel-title"><span className="dot" />Result Graph</div>
      <div className="absolute top-2 right-3 z-10 flex items-center gap-1.5">
        <button data-testid="result-zoom-in" onClick={() => fgRef.current?.zoom((fgRef.current?.zoom() || 1) * 1.4, 300)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70"><ZoomIn size={12} /></button>
        <button data-testid="result-zoom-out" onClick={() => fgRef.current?.zoom((fgRef.current?.zoom() || 1) / 1.4, 300)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70"><ZoomOut size={12} /></button>
        <button data-testid="result-zoom-fit" onClick={() => fgRef.current?.zoomToFit(400, 40)} className="border border-nexus-border p-1 hover:border-nexus-cyan hover:text-nexus-cyan text-nexus-text-2 bg-nexus-panel/70"><Maximize2 size={12} /></button>
      </div>

      {nodes.length === 0 ? (
        <div className="absolute inset-0 flex items-center justify-center text-nexus-text-2 font-mono text-[12px] uppercase tracking-widest">No matching agents. Try broadening the query.</div>
      ) : (
        <ForceGraph2D
          ref={fgRef}
          graphData={graphData}
          width={dims.w}
          height={dims.h}
          backgroundColor="rgba(4,9,20,0)"
          cooldownTicks={120}
          nodeRelSize={5}
          linkColor={(l) => INTENT_COLORS[l.intent] || "#334155"}
          linkWidth={(l) => Math.min(2.4, 0.35 + Math.log10(1 + (l.value_usd || 1)) * 0.55)}
          linkDirectionalParticles={(l) => (l.value_usd > 500 ? 2 : 1)}
          linkDirectionalParticleWidth={1.6}
          linkDirectionalParticleSpeed={0.006}
          onNodeHover={setHover}
          onNodeClick={(n) => onSelect(n.id)}
          nodeCanvasObject={(node, ctx, scale) => {
            const size = 3 + Math.log10(1 + node.tx_count) * 1.6;
            const color = STATUS_COLORS[node.status] || "#00F0FF";
            if (node.status !== "normal") {
              ctx.beginPath();
              ctx.arc(node.x, node.y, size + 5, 0, 2 * Math.PI);
              ctx.fillStyle = color + "22";
              ctx.fill();
            }
            ctx.beginPath();
            ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.strokeStyle = "#040914";
            ctx.lineWidth = 1;
            ctx.stroke();
            if (selectedId === node.id) {
              ctx.beginPath();
              ctx.arc(node.x, node.y, size + 8, 0, 2 * Math.PI);
              ctx.strokeStyle = "#00F0FF";
              ctx.lineWidth = 1.5;
              ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
            }
            const showLabel = scale > 1.4 || node.status !== "normal" || (hover && hover.id === node.id) || selectedId === node.id;
            if (showLabel) {
              const label = node.name;
              const fontSize = Math.max(10, 12 / Math.sqrt(scale));
              ctx.font = `600 ${fontSize}px Rajdhani`;
              const tw = ctx.measureText(label).width;
              ctx.fillStyle = "rgba(4,9,20,0.85)";
              ctx.fillRect(node.x - tw / 2 - 3, node.y - size - fontSize - 6, tw + 6, fontSize + 4);
              ctx.strokeStyle = color + "88";
              ctx.strokeRect(node.x - tw / 2 - 3, node.y - size - fontSize - 6, tw + 6, fontSize + 4);
              ctx.fillStyle = "#F8FAFC";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.fillText(label, node.x, node.y - size - fontSize / 2 - 4);
            }
          }}
        />
      )}
      {hover && !selectedId && (
        <div className="absolute bottom-3 left-3 panel px-3 py-2 text-[11px] font-mono max-w-[260px] z-10">
          <div className="text-nexus-cyan font-rajdhani font-semibold tracking-widest uppercase text-[12px]">{hover.name}</div>
          <div className="text-nexus-text-2 mt-1">{hover.framework} · {hover.chain}</div>
          <div className="text-nexus-text-3 mt-1 text-[10px] uppercase tracking-widest">click to inspect →</div>
        </div>
      )}
    </div>
  );
}

// -- Options: Cluster Overview (advanced) -----------------------------------

function ClusterOverview({ data, groupBy, showLabels }) {
  const containerRef = useRef();
  const fgRef = useRef();
  const [dims, setDims] = useState({ w: 900, h: 560 });
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const ro = new ResizeObserver((es) => { if (es[0]) setDims({ w: es[0].contentRect.width, h: es[0].contentRect.height }); });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const { groups, groupColor } = useMemo(() => {
    const g = new Map();
    data.nodes.forEach((n) => { const k = GROUP_FIELDS[groupBy](n) || "other"; g.set(k, (g.get(k) || 0) + 1); });
    const keys = Array.from(g.keys()).sort();
    const gc = new Map();
    keys.forEach((k, i) => gc.set(k, CLUSTER_TINTS[i % CLUSTER_TINTS.length]));
    return { groups: keys, groupColor: gc };
  }, [data.nodes, groupBy]);

  useEffect(() => {
    if (!fgRef.current) return;
    const fg = fgRef.current;
    const R = Math.min(dims.w, dims.h) * 0.34;
    const centers = new Map();
    groups.forEach((k, i) => {
      const a = (i / Math.max(1, groups.length)) * 2 * Math.PI - Math.PI / 2;
      centers.set(k, { x: Math.cos(a) * R, y: Math.sin(a) * R });
    });
    fg.d3Force("x", forceX((n) => centers.get(GROUP_FIELDS[groupBy](n) || "other")?.x ?? 0).strength(1.4));
    fg.d3Force("y", forceY((n) => centers.get(GROUP_FIELDS[groupBy](n) || "other")?.y ?? 0).strength(1.4));
    fg.d3Force("charge").strength(-260).distanceMax(180);
    const link = fg.d3Force("link");
    if (link) link.distance(70).strength(0.06);
    fg.d3ReheatSimulation();
  }, [groupBy, groups, dims, data]);

  return (
    <div ref={containerRef} className="relative brackets border border-nexus-border bg-nexus-panel/30 overflow-hidden" style={{ height: 620 }}>
      <span className="b1" /><span className="b2" />
      <div className="absolute inset-0 pointer-events-none z-10">
        {groups.map((k, i) => {
          const a = (i / Math.max(1, groups.length)) * 2 * Math.PI - Math.PI / 2;
          const x = 50 + Math.cos(a) * 44, y = 50 + Math.sin(a) * 44;
          return (
            <div key={k} className="absolute -translate-x-1/2 -translate-y-1/2 opacity-60" style={{ left: `${x}%`, top: `${y}%`, color: groupColor.get(k) }}>
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] whitespace-nowrap border border-current px-2 py-0.5 bg-nexus-bg/70 backdrop-blur-sm">{k}</div>
            </div>
          );
        })}
      </div>
      <ForceGraph2D
        ref={fgRef}
        graphData={{ nodes: data.nodes.map((n) => ({ ...n })), links: data.links.map((l) => ({ ...l })) }}
        width={dims.w} height={dims.h}
        backgroundColor="rgba(4,9,20,0)" cooldownTicks={140}
        nodeRelSize={5}
        linkColor={(l) => INTENT_COLORS[l.intent] || "#334155"}
        linkWidth={(l) => Math.min(2, 0.35 + Math.log10(1 + (l.value_usd || 1)) * 0.5)}
        linkDirectionalParticles={1}
        linkDirectionalParticleWidth={1.4}
        onNodeHover={setHover}
        nodeCanvasObject={(node, ctx, scale) => {
          const size = 3 + Math.log10(1 + node.tx_count) * 1.4;
          const status = STATUS_COLORS[node.status] || "#00F0FF";
          const tint = groupColor.get(GROUP_FIELDS[groupBy](node) || "other") || status;
          ctx.beginPath(); ctx.arc(node.x, node.y, size + 2.5, 0, 2 * Math.PI);
          ctx.strokeStyle = tint + "AA"; ctx.lineWidth = 1.2; ctx.stroke();
          if (node.status !== "normal") { ctx.beginPath(); ctx.arc(node.x, node.y, size + 5, 0, 2 * Math.PI); ctx.fillStyle = status + "22"; ctx.fill(); }
          ctx.beginPath(); ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
          ctx.fillStyle = status; ctx.fill();
          ctx.strokeStyle = "#040914"; ctx.lineWidth = 1; ctx.stroke();
          const show = showLabels === "all" || (showLabels === "smart" && (node.status !== "normal" || (hover && hover.id === node.id) || scale > 2.2));
          if (show) {
            const fs = Math.max(10, 12 / Math.sqrt(scale));
            ctx.font = `600 ${fs}px Rajdhani`;
            const tw = ctx.measureText(node.name).width;
            ctx.fillStyle = "rgba(4,9,20,0.85)";
            ctx.fillRect(node.x - tw / 2 - 3, node.y - size - fs - 6, tw + 6, fs + 4);
            ctx.strokeStyle = tint + "88";
            ctx.strokeRect(node.x - tw / 2 - 3, node.y - size - fs - 6, tw + 6, fs + 4);
            ctx.fillStyle = "#F8FAFC"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
            ctx.fillText(node.name, node.x, node.y - size - fs / 2 - 4);
          }
        }}
      />
    </div>
  );
}

// -- Main page --------------------------------------------------------------

export default function GraphWorkspace() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCluster, setShowCluster] = useState(false);
  const [clusterData, setClusterData] = useState({ nodes: [], links: [] });
  const [groupBy, setGroupBy] = useState("framework");
  const [labels, setLabels] = useState("smart");

  const runQuery = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await api.post("/graph/query", { query });
      setResult(r.data);
      setSelectedId(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showCluster && clusterData.nodes.length === 0) {
      api.get("/transactions/graph?top_agents=48").then((r) => setClusterData(r.data));
    }
  }, [showCluster, clusterData.nodes.length]);

  const clear = () => { setResult(null); setSelectedId(null); };

  return (
    <div className="p-4 space-y-4 relative">
      <div className="flex items-end justify-between">
        <div>
          <div className="panel-title">GRAPH WORKSPACE</div>
          {result ? (
            <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1 max-w-3xl">
              <span className="text-nexus-text-3">Q:</span> <span className="text-nexus-text">{result.query}</span>
            </h1>
          ) : (
            <h1 className="font-rajdhani font-bold text-2xl tracking-tight mt-1 opacity-0 select-none">Placeholder</h1>
          )}
        </div>
        <div className="flex items-center gap-2">
          {result && (
            <button
              data-testid="new-query-btn"
              onClick={clear}
              className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 border border-nexus-border hover:text-nexus-cyan hover:border-nexus-cyan px-2 py-1"
            >
              New Query
            </button>
          )}
          <button
            data-testid="open-settings-btn"
            onClick={() => setShowSettings(true)}
            className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-2 border border-nexus-border hover:text-nexus-cyan hover:border-nexus-cyan px-2 py-1 flex items-center gap-1"
          >
            <Settings2 size={12} /> Options
          </button>
        </div>
      </div>

      {!result && !showCluster && <Hero onRun={runQuery} loading={loading} />}

      {result && !showCluster && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3 relative">
          <ReasoningPanel result={result} />
          <ResultGraph nodes={result.nodes} links={result.links} onSelect={setSelectedId} selectedId={selectedId} />
          <AgentInspector agentId={selectedId} onClose={() => setSelectedId(null)} onTrace={() => {}} />
        </motion.div>
      )}

      {showCluster && (
        <div className="space-y-3 relative">
          <div className="panel px-3 py-2 flex flex-wrap items-center gap-3">
            <button
              data-testid="cluster-back-btn"
              onClick={() => setShowCluster(false)}
              className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/40 hover:bg-nexus-cyan/10 px-2 py-1"
            >
              ← Back to Query
            </button>
            <div className="h-4 w-px bg-nexus-border" />
            <Layers size={13} className="text-nexus-text-2" />
            <span className="panel-title">Group by</span>
            {Object.entries(GROUP_LABELS).map(([k, v]) => (
              <button
                key={k}
                data-testid={`group-by-${k}`}
                onClick={() => setGroupBy(k)}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${
                  groupBy === k ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/5" : "border-nexus-border text-nexus-text-2 hover:text-nexus-text"
                }`}
              >{v}</button>
            ))}
            <div className="h-4 w-px bg-nexus-border" />
            <span className="panel-title">Labels</span>
            {["smart", "all", "off"].map((m) => (
              <button
                key={m}
                data-testid={`labels-${m}`}
                onClick={() => setLabels(m)}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border flex items-center gap-1 ${
                  labels === m ? "border-nexus-cyan text-nexus-cyan bg-nexus-cyan/5" : "border-nexus-border text-nexus-text-2 hover:text-nexus-text"
                }`}
              >
                {m === "off" ? <EyeOff size={11} /> : m === "all" ? <Eye size={11} /> : <Focus size={11} />}
                {m}
              </button>
            ))}
          </div>
          <ClusterOverview data={clusterData} groupBy={groupBy} showLabels={labels} />
        </div>
      )}

      {/* Options / Settings modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="panel brackets max-w-lg w-full p-6"
            >
              <span className="b1" /><span className="b2" />
              <div className="flex justify-between items-start">
                <div>
                  <div className="panel-title"><Settings2 size={12} className="inline mr-1" />Workspace Options</div>
                  <h2 className="font-rajdhani font-bold text-xl mt-1 tracking-widest uppercase">Advanced Views</h2>
                </div>
                <button data-testid="close-settings" onClick={() => setShowSettings(false)} className="text-nexus-text-3 hover:text-nexus-text"><X size={16} /></button>
              </div>

              <div className="mt-5 space-y-3">
                <div className="border border-nexus-border p-3 hover:bg-nexus-panel-2/40">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-rajdhani font-semibold uppercase tracking-widest text-nexus-cyan">Cluster Overview</div>
                      <div className="text-[12px] text-nexus-text-2 mt-1">Radial map of all monitored agents grouped by framework, chain, risk, or region. Useful for structural reconnaissance — not for daily investigations.</div>
                    </div>
                    <button
                      data-testid="toggle-cluster-view"
                      onClick={() => { setShowCluster(true); setShowSettings(false); }}
                      className="text-[10px] font-mono uppercase tracking-widest text-nexus-cyan border border-nexus-cyan/50 hover:bg-nexus-cyan/10 px-2 py-1 shrink-0"
                    >
                      Open
                    </button>
                  </div>
                </div>

                <div className="border border-nexus-border p-3 opacity-60">
                  <div className="font-rajdhani font-semibold uppercase tracking-widest text-nexus-text-2">Query History <span className="text-[10px] text-nexus-text-3 ml-2">// coming soon</span></div>
                  <div className="text-[12px] text-nexus-text-2 mt-1">Recent NL queries with re-run and diff.</div>
                </div>
                <div className="border border-nexus-border p-3 opacity-60">
                  <div className="font-rajdhani font-semibold uppercase tracking-widest text-nexus-text-2">Saved Investigations <span className="text-[10px] text-nexus-text-3 ml-2">// coming soon</span></div>
                  <div className="text-[12px] text-nexus-text-2 mt-1">Persist a query + agent selection into a case file with LLM briefing.</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
